import { Fragment, useState } from 'react';
import styles from './styles/CalendarModal.module.scss';
import Clickable from './Clickable';
import Chevron from 'icons/Chevron';
import cn from 'classnames';
import Modal from './Modal';

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function generateCalendarMatrix(date: Date) {
  const matrix: (number | undefined)[][] = [];

  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1).getDay();

  let maxDays = daysInMonth[month];
  if (month == 1) {
    // February leap year calculation
    if ((year % 4 == 0 && year % 100 != 0) || year % 400 == 0) {
      maxDays += 1;
    }
  }

  let counter = 1;
  for (let row = 0; row < 6 /* Math.ceil((maxDays + firstDay) / 7) */; row++) {
    matrix[row] = [];
    for (let col = 0; col < 7; col++) {
      if ((row > 0 || col >= firstDay) && counter <= maxDays) {
        matrix[row][col] = counter++;
      } else {
        matrix[row][col] = undefined;
      }
    }
  }

  return matrix;
}

interface CalendarModalProps {
  value: Date;
  makeDatePath: (date: Date) => string | undefined;
  onClose: () => void;
  onChangeMonth?: (date: Date) => void;
}

export default function CalendarModal({ value, onClose, makeDatePath, onChangeMonth }: CalendarModalProps) {
  const [activeDate, setActiveDate] = useState(value);
  const today = new Date();

  function changeActiveDate({ month, day }: { month?: number; day?: number }) {
    const newDate = new Date(activeDate.valueOf());
    if (month != undefined) newDate.setMonth(month);
    if (day != undefined) newDate.setDate(day);
    return newDate;
  }

  function isSameDate(day: number, date: Date) {
    return date.getDate() === day && date.getMonth() === activeDate.getMonth() && date.getFullYear() === activeDate.getFullYear();
  }

  function changeMonth(n: 1 | -1) {
    const newDate = changeActiveDate({ month: activeDate.getMonth() + n });
    setActiveDate(newDate);
    onChangeMonth && onChangeMonth(newDate);
  }

  return (
    <Modal onClose={onClose}>
      <div className={styles.Calendar}>
        <div className={styles.Header}>
          <Clickable className={styles.PreviousMonth} onClick={() => changeMonth(-1)}>
            <Chevron direction="left" height={10} color="#000" />
          </Clickable>
          <div className={styles.Month}>
            {months[activeDate.getMonth()]} {activeDate.getFullYear()}
          </div>
          <Clickable className={styles.NextMonth} onClick={() => changeMonth(1)}>
            <Chevron direction="right" height={10} color="#000" />
          </Clickable>
        </div>
        <div className={styles.Grid}>
          {weekDays.map(weekDay => (
            <div key={weekDay} className={styles.DayLabel}>
              {weekDay}
            </div>
          ))}
          <div className={styles.Divider} />
          {generateCalendarMatrix(activeDate).map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              {row.map((day, colIndex) => {
                if (!day) return <div key={colIndex} />;

                const href = makeDatePath(changeActiveDate({ day }));

                if (!href) {
                  return (
                    <div key={colIndex} className={cn(styles.Day, { [styles.Today]: isSameDate(day, today) })}>
                      {day}
                    </div>
                  );
                } else {
                  return (
                    <Clickable
                      href={href}
                      key={colIndex}
                      className={cn(styles.Day, styles.ActiveDay, {
                        [styles.SelectedDay]: isSameDate(day, value),
                        [styles.Today]: isSameDate(day, today)
                      })}
                    >
                      {day}
                    </Clickable>
                  );
                }
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </Modal>
  );
}
